import { v4 as uuidv4 } from 'uuid'
import { Dropdown } from 'rsuite'
import { DndContext, DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { Button, ButtonGroup } from '@mui/material'
import { DraggableItem } from './DragInvoice'
import { useIntl } from 'react-intl'
import { FC, useEffect, useRef, useState } from 'react'
import { InvoiceConfig, InvoicePages } from '../../interfaces/Invoices'
import { useSettingsStore } from '../../stores/SettingsStore'
import { InvoiceModal } from './InvoiceModal'

interface ContainerDnDInvoiceProps {
  routerContext: 'invoice' | 'pipeline' | 'web-view-preview';
  stepType: 'config' | 'preview';
  column: any[];
  extractPages: boolean;
  pageItems: InvoicePages;
  pageRef?: (el: HTMLDivElement | null) => void;
  setMultiPages?: React.Dispatch<React.SetStateAction<InvoicePages[] | null>>
}

export const ContainerDnDInvoice: FC<ContainerDnDInvoiceProps> = ({
  routerContext = 'invoice',
  stepType = 'config',
  column,
  extractPages,
  pageItems,
  pageRef,
  setMultiPages
}) => {
  const { settings } = useSettingsStore()
  const [modeDnD, setModeDnD] = useState<'edit' | 'preview'>('edit')
  const itemRef = useRef(null)
  const [typeInvoice, setTypeInvoice] = useState<'all' | 'pipeline' | 'pipeline-multi-product' | 'pipeline-cf' | 'pipeline-multi-cf'>('pipeline')
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [previewItems, setPreviewItems] = useState<InvoiceConfig[]>([])
  const [items, setItems] = useState<InvoiceConfig[]>([])
  const [item, setItem] = useState<any>()
  const [contextDnd, setContextDnd] = useState<'edit' | 'add'>('edit')
  const [showFormDnD, setShowFormDnD] = useState(false)
  const handleFormDnDOpen = () => { setShowFormDnD(true); setContextDnd('edit') }
  const handleFormDnDClose = (dnd_type?: 'add' | 'edit') => {
    setShowFormDnD(false);
    if (dnd_type == 'add') {
      setItems((prevItems) => prevItems.slice(0, -1))
    }
    setContextDnd('edit')
  }
  const intl = useIntl()

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event
    const activeItem = items.find((item) => item.id === active.id)

    if (activeItem) {
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.id === activeItem.id ? { ...item, zIndex: 2 } : { ...item, zIndex: 0 }
        )
      )
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event
    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === active.id) {
          return {
            ...item,
            dnd_data: {
              ...item.dnd_data,
              position: {
                x: Math.max(
                  0,
                  Math.min(item.dnd_data.position.x + delta.x, pageItems.imageFile!.width - 100)
                ),
                y: Math.max(
                  0,
                  Math.min(item.dnd_data.position.y + delta.y, pageItems.imageFile!.height - 50)
                ),
              },
            },
          }
        }
        return item
      })
    )
  }

  const addNewItem = (type: 'pipeline' | 'pipeline-multi-product' | 'pipeline-cf') => {
    setTypeInvoice(type)
    const newItem: InvoiceConfig = {
      id: uuidv4(),
      name: `Item-${items.length + 1}`,
      model: type,
      dnd_data: {
        font: {
          size: 12,
          name: 'Roboto',
        },
        spacing: 4,
        reference: type === 'pipeline-multi-product' ? 'products' : 'not_set',
        position: {
          x: (pageItems.imageFile!.width - 100) / 2,
          y: (pageItems.imageFile!.height - 100) / 2,
        },
        textCase: 'none',
        allignment: 'left',
        formattedContent: '',
        content: [],
      },
    }
    setItems([...items, newItem])
    setShowFormDnD(true)
    setItem(newItem)
  }

  const clearItems = () => {
    setItems([])
  }

  const handleSaveFont = (id: string, font: { size: number; name: string }) =>
    setItems(items.map((item) => (item.id === id ? { ...item, dnd_data: { ...item.dnd_data, font } } : item)))

  const handlePreviewQty = (id: string, qty: number, spacing: number = 1) => {
    const getItem = typeof previewItems?.find((item) => item.id === id) === 'undefined' ? items.find((item) => item.id === id) : previewItems?.find((item) => item.id === id)
    const parsedHtml = getItem?.dnd_data.formattedContent?.replace(/<p>(.*?)<\/p>/g, '<p style="margin-bottom:0px !important; line-height: 8px;">$1</p>') || ''
    let previewQtyHtml = ''
    for (let i = 0; i < qty; i++) {
      previewQtyHtml += `<div style="margin-bottom: ${spacing + 'px'}; ${i == 0 ? 'margin-top: 6px' : ''}">${parsedHtml}</div>`;
    }

    setItems(items.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          dnd_data: {
            ...item.dnd_data,
            previewQty: qty,
            previewQtyHtml: previewQtyHtml,
            spacing
          }
        };
      }
      return item;
    }));
  }

  const handleDeleteItem = (id: string) => {
    setItems(items.filter((item) => item.id !== id))
  }

  const prevPageItemsRef = useRef(pageItems);

  useEffect(() => {
    if (stepType === "config" && setMultiPages && extractPages) {
      // Cek apakah nilai `items` benar-benar berubah sebelum update `setMultiPages`
      setMultiPages((prevItems) => {
        if (prevItems) {
          return prevItems.map((item) =>
            item.id === pageItems.id
              ? { ...item, fields: items }
              : item
          );
        }
        return [{ ...pageItems, fields: items }];
      });
    }
  }, [extractPages, items]); // `pageItems` sengaja dihapus dari dependensi untuk mencegah loop

  useEffect(() => {
    if (stepType === "preview" && pageItems.fields) {
      setItems(pageItems.fields);
      setModeDnD("preview");
    }

    if (stepType === "config" && pageItems.fields) {
      if (JSON.stringify(prevPageItemsRef.current.fields) !== JSON.stringify(pageItems.fields)) {
        setItems(pageItems.fields);
        prevPageItemsRef.current = pageItems; // Simpan data terbaru
      }
    }
  }, [stepType, pageItems]); // Tetap menjaga `pageItems` dalam dependensi


  useEffect(() => {
    if (pageItems.fields && pageItems.fields.length > 0) {
      setItems(pageItems.fields);
    }
  }, []);

  return (
    <>
      <div className='modal-body p-0'>
        {pageItems && (
          <div style={{ background: stepType === 'config' ? '#6c6c6c' : 'transparent' }}>
            <DndContext
              onDragStart={(e) => {
                if (stepType == 'config') {
                  handleDragStart(e)
                }
              }}
              onDragEnd={(e) => {
                if (stepType == 'config') {
                  handleDragEnd(e)
                }
              }}
            >
              <div
                className='container-md d-flex flex-column justify-content-center align-items-center gap-4 pt-8 pb-12'
                style={{
                  position: 'relative',
                  maxWidth: '1080px',
                  width: '100%',
                }}
              >
                {stepType === 'config' && (
                  <div
                    style={{
                      width: `720px`,
                      border: '1px solid #ccc',
                    }}
                    className='d-flex justify-content-between p-4 gap-4 bg-white rounded'
                  >
                    <div className='d-flex flex-column gap-2'>
                      <small className='fw-bold'>Drag & Drop Text Items</small>
                      <div className='d-flex flex-row gap-2'>
                        <Dropdown
                          renderToggle={(props: any, ref: any) => {
                            return (
                              <button
                                {...props}
                                ref={ref}
                                type='button'
                                className='d-flex items-center gap-2 btn btn-success btn-sm'
                              >
                                {intl.formatMessage({ id: 'FORM.LABEL.GENERAL_ADD' }, { title: 'Item' })}
                              </button>
                            )
                          }}
                          title={`${intl.formatMessage({ id: 'FORM.ACTION.ADD' })}`}
                          noCaret
                          placement='bottomEnd'
                        >
                          <Dropdown.Item
                            onClick={() => {
                              addNewItem('pipeline-cf')
                              setContextDnd('add')
                            }}
                          >
                            {settings.pipeline_title}{' '}
                            {intl.formatMessage({ id: 'FORM.LABEL.VARIABLE' })}
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              addNewItem('pipeline-multi-product')
                              setContextDnd('add')
                            }}
                          >
                            {settings.pipeline_title} Multi-
                            {intl.formatMessage({ id: 'MENU.PRODUCTS' })}{' '}
                            {intl.formatMessage({ id: 'FORM.LABEL.VARIABLE' })}
                          </Dropdown.Item>
                        </Dropdown>
                        <button
                          onClick={clearItems}
                          type='button'
                          className='d-flex items-center gap-2 btn btn-danger btn-sm'
                        >
                          {intl.formatMessage({ id: 'FORM.LABEL.GENERAL_CLEAR' }, { title: 'Item' })}
                        </button>
                      </div>
                    </div>
                    <div className='d-flex flex-column gap-2'>
                      <small className='fw-bold text-right'>
                        {intl.formatMessage({ id: 'FORM.LABEL.EDIT_PANEL' })}
                      </small>
                      <div className='d-flex justify-content-between'>
                        <ButtonGroup variant='outlined' aria-label='Basic button group'>
                          <Button
                            className={modeDnD === 'edit' ? 'bg-primary text-white' : ''}
                            onClick={() => setModeDnD('edit')}
                          >
                            Set Text
                          </Button>
                          <Button
                            className={modeDnD === 'preview' ? 'bg-primary text-white' : ''}
                            onClick={() => setModeDnD('preview')}
                          >
                            Set View
                          </Button>
                        </ButtonGroup>
                      </div>
                    </div>
                  </div>
                )}
                <div
                  ref={itemRef}
                  className='custom-scroll'
                  style={{
                    maxWidth: `100%`,
                    minWidth: `400px`,
                    maxHeight: `1280px`,
                    minHeight: `300px`,
                    backgroundColor: '#fff',
                    boxSizing: 'border-box',
                    position: 'relative',
                    overflow: 'auto',
                  }}
                >
                  <div
                    ref={pageRef}
                    style={{
                      width: `${pageItems.imageFile?.width ?? '400'}px`,
                      height: `${pageItems.imageFile?.height ?? '400'}px`,
                      backgroundImage: pageItems.imageFile?.value ? `url(${pageItems.imageFile?.value})` : 'none',
                      backgroundPosition: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      margin: '0 auto',
                    }}
                  >
                    {items.map((item) => (
                      <DraggableItem
                        key={item.id}
                        id={item.id}
                        routerContext={routerContext}
                        stepType={stepType}
                        item={item}
                        setItem={setItem}
                        previewItems={previewItems}
                        handleFormDnDOpen={handleFormDnDOpen}
                        openMenuId={openMenuId}
                        setOpenMenuId={setOpenMenuId}
                        modeDnD={modeDnD}
                        column={column}
                        handleSaveFont={handleSaveFont}
                        handlePreviewQty={handlePreviewQty}
                        handleDeleteItem={handleDeleteItem}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </DndContext>
          </div>
        )}
      </div>
      {routerContext === 'invoice' && (
        <InvoiceModal
          context={contextDnd}
          typeInvoice={typeInvoice}
          item={item}
          setItem={setItem}
          showFormDnD={showFormDnD}
          handleFormDnDClose={handleFormDnDClose}
          setItems={setItems}
          column={column}
          setPreviewItems={setPreviewItems}
        />
      )}
    </>
  )
}
export default ContainerDnDInvoice